import React from 'react';
import {Container, Alert, Button} from 'react-bootstrap';
import {useLocation, Link} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useAuth} from '../auth/auth-context';
import {fetchVersionInfo} from '../libs/version';
import type {RootState} from '../store';

function AppUpdateAlert(): React.JSX.Element {
  const [stale, setStale] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);
  const currentVersion = useSelector((state: RootState) => state.version.data);
  const {refreshAuthLoading} = useAuth();

  const checkForUpdate = React.useCallback(
    async (aliveRef?: {current: boolean}) => {
      try {
        const latest = await fetchVersionInfo();
        if (aliveRef && !aliveRef.current) {
          return;
        }

        const latestBuildId = latest?.buildId ?? undefined;
        const currentBuildId = currentVersion?.buildId ?? undefined;
        if (!currentBuildId || !latestBuildId) {
          return;
        }

        setStale(latestBuildId !== currentBuildId);
      } catch {
        // ignore errors to avoid spamming the user; we'll check again later
      }
    },
    [currentVersion?.buildId],
  );

  React.useEffect(() => {
    if (refreshAuthLoading) {
      return;
    }

    const alive = {current: true};
    void checkForUpdate(alive);
    return () => {
      alive.current = false;
    };
  }, [checkForUpdate, refreshAuthLoading]);

  if (!stale || dismissed) {
    return <></>;
  }

  return (
    <Alert
      variant='info'
      onClose={() => {
        setDismissed(true);
      }}
      dismissible
      className='d-flex align-items-center justify-content-between'
    >
      <div className='me-3'>
        A new version of the app is available. Please refresh the page to
        update.
      </div>
      <div>
        <Button
          size='sm'
          variant='primary'
          onClick={() => {
            try {
              globalThis.location.reload();
            } catch {
              globalThis.location.assign(globalThis.location.href);
            }
          }}
        >
          Refresh
        </Button>
      </div>
    </Alert>
  );
}

export function ShowAlerts(): React.JSX.Element {
  const location = useLocation();
  const {isAuthenticated, emailVerified, loginLoading} = useAuth();
  const [showEmailVerifiedAlert, setShowEmailVerifiedAlert] =
    React.useState(true);

  if (loginLoading) {
    return <></>;
  }

  return (
    <Container>
      {isAuthenticated && <AppUpdateAlert />}
      {isAuthenticated &&
        !loginLoading &&
        !emailVerified &&
        showEmailVerifiedAlert &&
        location.pathname !== '/verify_email' && (
          <Alert
            variant='warning'
            onClose={() => {
              setShowEmailVerifiedAlert(false);
            }}
            dismissible
          >
            Your email is not verified. Please check your email for a message to
            verify it. Click the link{' '}
            <Link to='/resend_email_verification'>here</Link> to re-send the
            verification email.
          </Alert>
        )}
    </Container>
  );
}

export function BasicAiAlert(): React.JSX.Element {
  return (
    <Alert variant='info' title='Note'>
      <h4>Note</h4>
      AI-generated content may be inaccurate or biased. Verify important
      information and avoid sharing sensitive data.
    </Alert>
  );
}

const alerts = {
  ShowAlerts,
  BasicAiAlert,
};

export default alerts;
